import type { TImageResize } from './ResponsiveImage.types'

type TImageCandidatesArgs = {
    src: string
    resize?: TImageResize
    /** Set for a fixed box: switches the ladder to `1x`/`2x`, the way `next/image` does. */
    width?: number
    sizes?: string
}

const VIEWPORT_WIDTH = /(^|\s)(1?\d?\d)vw/g

const snap = (widths: number[], target: number) =>
    widths.find(width => width >= target) ?? Math.max(...widths)

/**
 * `next/image`'s `getWidths`: a `sizes` written in `vw` can never need a candidate below its
 * smallest viewport share, so the ladder is cut there — a `100vw` hero ships 9 candidates, not 17.
 */
const resolveWidths = ({ widths, deviceWidths }: TImageResize, sizes: string | undefined) => {
    // Unreachable from product code today — every call site passes a `sizes` or a `width` — but it
    // is `next/image`'s answer for a fluid box that declares neither, so it stays.
    if (!sizes) {
        return deviceWidths.length ? deviceWidths : widths
    }

    const shares = [...sizes.matchAll(VIEWPORT_WIDTH)].map(match => Number(match[2]))

    if (!shares.length || !deviceWidths.length) {
        return widths
    }

    const floor = deviceWidths[0] * (Math.min(...shares) / 100)
    const reachable = widths.filter(width => width >= floor)

    // A ladder that shares no widths with its device half would leave nothing to offer, and
    // `Math.max()` of nothing is `-Infinity` — which reaches the browser as a src.
    return reachable.length ? reachable : widths
}

/**
 * The two ladders `next/image` builds, kept in one place so every consumer emits the same shape:
 * a fixed box gets a density pair and a `sizes`-driven box gets the widths `sizes` can reach.
 */
export const buildImageCandidates = ({ src, resize, width, sizes }: TImageCandidatesArgs) => {
    // `buildSrc` appends the resize spec, so an empty source yields `@720?…` — an address the browser
    // resolves against the page and requests.
    if (!src || !resize?.widths.length) {
        return { src, srcSet: undefined }
    }

    const { widths, buildSrc } = resize

    if (width) {
        const oneX = snap(widths, width)
        const twoX = snap(widths, width * 2)

        return {
            src: buildSrc(src, twoX),
            srcSet: `${buildSrc(src, oneX)} 1x, ${buildSrc(src, twoX)} 2x`,
        }
    }

    const candidates = resolveWidths(resize, sizes)

    return {
        src: buildSrc(src, Math.max(...candidates)),
        srcSet: candidates.map(candidate => `${buildSrc(src, candidate)} ${candidate}w`).join(', '),
    }
}
